import React, { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAbortableLoad, isCanceled } from '../hooks/useAbortableLoad';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft, Loader2, User, Briefcase, FileText, Home, Building2, Layers,
  CreditCard, Gauge, MapPin, Phone, Mail, Calendar, AlertTriangle, MessageSquare,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts';

const fmtMoney = (v, mon) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return `${mon || ''} ${n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
};
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
};

const Dato = ({ label, value, icon: Icon }) => (
  <div className="min-w-0">
    <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className="text-sm text-slate-800 truncate flex items-center gap-1">
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
      {value || '—'}
    </p>
  </div>
);

const estadoColor = (estado) => {
  const e = (estado || '').toLowerCase();
  if (e.includes('venta') || e.includes('entregad')) return 'bg-green-100 text-green-800';
  if (e.includes('proceso')) return 'bg-blue-100 text-blue-800';
  if (e.includes('vencid')) return 'bg-red-100 text-red-800';
  return 'bg-slate-100 text-slate-700';
};

// Nombre compacto del punto para el eje X.
const cortarNombre = (n) => (!n ? '—' : n.length > 12 ? `${n.slice(0, 11)}…` : n);

const CjmTooltip = ({ active, payload, label, data, dataKey, unidad }) => {
  if (!active || !payload?.length) return null;
  const item = data[label];
  if (!item) return null;
  const val = item[dataKey];
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 shadow-sm text-xs">
      <p className="font-medium text-slate-800">{item.label}</p>
      <p className="text-slate-400">{item.fecha}</p>
      <p className="mt-1 text-slate-700">{unidad}: <b>{val ?? '—'}</b></p>
    </div>
  );
};

const npsDotColor = (n) => (n == null ? '#cbd5e1' : n >= 9 ? '#16a34a' : n >= 7 ? '#f59e0b' : '#dc2626');

const CjmChart = ({ titulo, data, dataKey, color, domain, ticks, testid, coloredByNps }) => {
  const renderDot = (props) => {
    const { cx, cy, payload, index } = props;
    if (cx == null || cy == null || payload?.[dataKey] == null) return <g key={index} />;
    const c = npsDotColor(payload.nps);
    return <circle key={index} cx={cx} cy={cy} r={4} fill={c} stroke="#fff" strokeWidth={1.5} />;
  };
  return (
  <div data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">{titulo}</p>
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={data} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis
          dataKey="idx"
          interval={0}
          height={70}
          tickMargin={8}
          angle={-35}
          textAnchor="end"
          tick={{ fontSize: 9, fill: '#475569' }}
          tickFormatter={(v) => cortarNombre(data[v]?.label)}
          stroke="#cbd5e1"
        />
        <YAxis domain={domain} ticks={ticks} fontSize={11} stroke="#cbd5e1" allowDecimals={false} />
        <RechartsTooltip content={<CjmTooltip data={data} dataKey={dataKey} unidad={titulo.split(' ')[0]} />} />
        <Line
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          strokeWidth={2}
          dot={coloredByNps ? renderDot : { r: 3, fill: color }}
          connectNulls
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
  );
};

const Cliente360DetailPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const res = await api.get(`/experiencia/clientes/${id}`, { signal });
      setData(res.data);
    } catch (e) {
      if (isCanceled(e)) return;
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api, id]);

  useAbortableLoad((signal) => load(signal), [load]);

  if (loading) {
    return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (!data) {
    return (
      <div className="p-12 text-center text-slate-500" data-testid="cliente360-notfound">
        Cliente no encontrado.
        <div className="mt-4"><Button variant="outline" onClick={() => navigate('/admin/experiencia/clientes')}><ArrowLeft className="w-4 h-4 mr-2" />Volver</Button></div>
      </div>
    );
  }

  const { cliente, proformas } = data;
  const semaforo = data.semaforo || {};
  const historial = data.semaforo_historial || [];
  const semColor = { verde: '#16a34a', ambar: '#f59e0b', rojo: '#dc2626' }[semaforo.semaforo] || '#cbd5e1';

  // Data CJM: encuestas ordenadas ascendentemente por fecha (cada encuesta = 1 punto en el eje X).
  const cjmData = [...historial]
    .sort((a, b) => (a.fecha || a.created_at || '').localeCompare(b.fecha || b.created_at || ''))
    .map((h, i) => ({
      idx: i,
      label: h.punto_nombre || h.codigo_punto_contacto || 'Sin punto',
      fecha: fmtDate(h.fecha || h.created_at),
      nps: h.nps ?? null,
      csat: h.csat ?? null,
    }));

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="cliente360-detail-page">
      <button onClick={() => navigate('/admin/experiencia/clientes')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 w-fit transition-colors" data-testid="volver-clientes-btn">
        <ArrowLeft className="w-4 h-4" /> Volver a Cliente 360
      </button>

      {/* Cabecera del cliente */}
      <Card className="border-slate-200">
        <CardContent className="p-5 lg:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                <User className="w-6 h-6 text-slate-500" />
              </div>
              <div className="min-w-0">
                <h1 className="font-heading text-xl font-bold text-slate-900 truncate">{cliente.nombre}</h1>
                <p className="text-sm text-slate-500">
                  {cliente.tipo_documento} {cliente.documento || ''} · {cliente.tipo_persona || ''}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {cliente.es_inversionista && (
                    <Badge className="bg-amber-100 text-amber-800 border-0 font-normal" data-testid="badge-inversionista"><Briefcase className="w-3 h-3 mr-1" />Inversionista</Badge>
                  )}
                  {semaforo.es_critico && (
                    <Badge className="bg-red-100 text-red-800 border-0 font-normal" data-testid="badge-critico"><AlertTriangle className="w-3 h-3 mr-1" />Cliente Crítico</Badge>
                  )}
                  {cliente.estado && <Badge variant="outline" className="font-normal">{cliente.estado}</Badge>}
                  <Badge variant="outline" className="font-normal"><FileText className="w-3 h-3 mr-1" />{proformas.length} proforma(s)</Badge>
                </div>
              </div>
            </div>
            {/* Semáforo */}
            <div className="rounded-lg border px-4 py-2 text-center shrink-0 min-w-[140px]" style={{ borderColor: `${semColor}55` }} data-testid="semaforo-box">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 flex items-center gap-1 justify-center"><Gauge className="w-3.5 h-3.5" /> Semáforo</p>
              {semaforo.semaforo ? (
                <div className="mt-1">
                  <div className="flex items-center justify-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: semColor }} />
                    <span className="text-sm font-semibold" style={{ color: semColor }} data-testid="semaforo-clasificacion">{semaforo.clasificacion}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">NPS {semaforo.nps ?? '—'} · CSAT {semaforo.csat ?? '—'}</p>
                </div>
              ) : (
                <p className="text-sm text-slate-400 mt-1" data-testid="semaforo-sin-datos">Sin encuestas aún</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Datos personales (compacto) */}
      <Card className="border-slate-200">
        <CardContent className="p-5 lg:p-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><User className="w-4 h-4" /> Datos Personales</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3">
            <Dato label="Correo" value={cliente.email} icon={Mail} />
            <Dato label="Celular" value={cliente.celular} icon={Phone} />
            <Dato label="Estado civil" value={cliente.estado_civil} />
            <Dato label="Género" value={cliente.genero} />
            <Dato label="Fecha nacimiento" value={fmtDate(cliente.fecha_nacimiento)} icon={Calendar} />
            <Dato label="Nacionalidad" value={cliente.nacionalidad} />
            <Dato label="Ocupación" value={cliente.ocupacion} />
            <Dato label="Documento cónyuge" value={cliente.documento_conyuge} />
            <Dato label="Dirección" value={cliente.direccion} icon={MapPin} />
            <Dato label="Distrito" value={cliente.distrito} />
            <Dato label="Medio captación" value={cliente.medio_captacion} />
            <Dato label="Canal entrada" value={cliente.canal_entrada} />
          </div>
        </CardContent>
      </Card>

      {/* Proformas */}
      <Card className="border-slate-200">
        <CardContent className="p-5 lg:p-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Proformas</h2>
          {proformas.length === 0 ? (
            <p className="text-sm text-slate-400">Este cliente no tiene proformas registradas.</p>
          ) : (
            <Accordion type="single" collapsible className="w-full" defaultValue={proformas[0]?.id}>
              {proformas.map((pf) => (
                <AccordionItem key={pf.id} value={pf.id} data-testid={`proforma-${pf.id}`}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex flex-1 items-center justify-between gap-3 pr-3 text-left">
                      <div className="min-w-0">
                        <span className="font-mono text-sm font-medium text-slate-900">{pf.codigo_proforma}</span>
                        <span className="text-sm text-slate-500 ml-2">{pf.nombre_proyecto || pf.codigo_proyecto}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {pf.tipo_titular && <Badge variant="outline" className="font-normal capitalize hidden sm:inline-flex">{pf.tipo_titular}{pf.porct_participacion != null ? ` ${pf.porct_participacion}%` : ''}</Badge>}
                        <Badge className={`border-0 font-normal capitalize ${estadoColor(pf.estado)}`}>{pf.estado || '—'}</Badge>
                        <span className="text-sm font-semibold text-slate-700 hidden md:inline">{fmtMoney(pf.precio_venta, pf.moneda)}</span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 mb-3 px-1">
                      <Dato label="Proyecto" value={`${pf.codigo_proyecto || ''} ${pf.nombre_proyecto || ''}`.trim()} icon={Building2} />
                      <Dato label="Precio venta" value={fmtMoney(pf.precio_venta, pf.moneda)} />
                      <Dato label="Financiamiento" value={pf.tipo_financiamiento} />
                      <Dato label="Banco" value={pf.banco} />
                      <Dato label="Fecha creación" value={fmtDate(pf.fecha_creacion)} icon={Calendar} />
                      <Dato label="Plantilla" value={pf.nombre_plantilla} />
                    </div>
                    <Separator className="my-3" />
                    <Tabs defaultValue="unidades">
                      <TabsList className="flex-wrap h-auto">
                        <TabsTrigger value="unidades" data-testid={`tab-unidades-${pf.id}`}>Unidades ({pf.unidades.length})</TabsTrigger>
                        <TabsTrigger value="adendas">Adendas ({pf.adendas.length})</TabsTrigger>
                        <TabsTrigger value="entrega">Entrega ({pf.entrega.length})</TabsTrigger>
                        <TabsTrigger value="cronograma">Cronograma ({pf.cronograma.length})</TabsTrigger>
                      </TabsList>

                      {/* UNIDADES + trazabilidad Etapa */}
                      <TabsContent value="unidades" className="mt-3">
                        {pf.unidades.length === 0 ? <p className="text-xs text-slate-400">Sin unidades.</p> : (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="table-header">
                                  <TableHead>Unidad</TableHead>
                                  <TableHead>Tipo</TableHead>
                                  <TableHead><Layers className="w-3.5 h-3.5 inline mr-1" />Etapa</TableHead>
                                  <TableHead><Home className="w-3.5 h-3.5 inline mr-1" />Bloque</TableHead>
                                  <TableHead className="text-right">Área (m²)</TableHead>
                                  <TableHead className="text-right">Precio venta</TableHead>
                                  <TableHead>Estado</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {pf.unidades.map((u) => (
                                  <TableRow key={u.id} className="table-row">
                                    <TableCell className="font-mono text-xs">{u.codigo}<div className="text-slate-500">{u.nombre}</div></TableCell>
                                    <TableCell className="text-sm capitalize">{u.tipo_unidad}{u.nombre_tipologia ? ` · ${u.nombre_tipologia}` : ''}</TableCell>
                                    <TableCell className="text-sm">
                                      {u.etapa_codigo
                                        ? <Badge variant="outline" className="font-mono text-xs">{u.etapa_codigo}</Badge>
                                        : <span className="text-xs text-slate-400" title="No resuelto desde Subdivisiones">—</span>}
                                    </TableCell>
                                    <TableCell className="font-mono text-xs text-slate-500">{u.codigo_subdivision || '—'}</TableCell>
                                    <TableCell className="text-right text-sm">{u.area_total ?? '—'}</TableCell>
                                    <TableCell className="text-right text-sm">{fmtMoney(u.precio_venta, pf.moneda)}</TableCell>
                                    <TableCell className="text-sm capitalize">{u.estado_comercial || '—'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </TabsContent>

                      {/* ADENDAS (fase personalizado) */}
                      <TabsContent value="adendas" className="mt-3">
                        {pf.adendas.length === 0 ? <p className="text-xs text-slate-400">Sin adendas.</p> : (
                          <ul className="space-y-1.5">
                            {pf.adendas.map((a) => (
                              <li key={a.id} className="text-sm flex items-center justify-between border-b border-slate-100 pb-1.5">
                                <span>{a.nombre} {a.nombre_flujo && <span className="text-slate-400">· {a.nombre_flujo}</span>}</span>
                                <span className="text-xs text-slate-500">{fmtDate(a.fecha_inicio)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </TabsContent>

                      {/* ENTREGA */}
                      <TabsContent value="entrega" className="mt-3">
                        {pf.entrega.length === 0 ? <p className="text-xs text-slate-400">Sin datos de entrega.</p> : (
                          <ul className="space-y-1.5">
                            {pf.entrega.map((a) => (
                              <li key={a.id} className="text-sm flex items-center justify-between border-b border-slate-100 pb-1.5">
                                <span>{a.nombre} {a.nombre_flujo && <span className="text-slate-400">· {a.nombre_flujo}</span>}</span>
                                <span className="text-xs text-slate-500">{fmtDate(a.fecha_inicio)} → {fmtDate(a.fecha_fin)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </TabsContent>

                      {/* CRONOGRAMA DE PAGOS */}
                      <TabsContent value="cronograma" className="mt-3">
                        {pf.cronograma.length === 0 ? <p className="text-xs text-slate-400">Sin cronograma.</p> : (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="table-header">
                                  <TableHead><CreditCard className="w-3.5 h-3.5 inline mr-1" />Cuota</TableHead>
                                  <TableHead>Vencimiento</TableHead>
                                  <TableHead className="text-right">Programado</TableHead>
                                  <TableHead className="text-right">Pagado</TableHead>
                                  <TableHead className="text-right">Saldo</TableHead>
                                  <TableHead>Estado</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {pf.cronograma.map((cr) => (
                                  <TableRow key={cr.id} className="table-row">
                                    <TableCell className="text-sm">{cr.etiqueta || cr.nombre}</TableCell>
                                    <TableCell className="text-sm">{fmtDate(cr.fecha_vcto)}</TableCell>
                                    <TableCell className="text-right text-sm">{fmtMoney(cr.monto_programado, pf.moneda)}</TableCell>
                                    <TableCell className="text-right text-sm">{fmtMoney(cr.monto_pagado, pf.moneda)}</TableCell>
                                    <TableCell className="text-right text-sm">{fmtMoney(cr.saldo, pf.moneda)}</TableCell>
                                    <TableCell><Badge className={`border-0 font-normal capitalize ${estadoColor(cr.estado)}`}>{cr.estado || '—'}</Badge></TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </TabsContent>
                    </Tabs>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Semáforo (NPS/CSAT) */}
      <Card className="border-slate-200">
        <CardContent className="p-5 lg:p-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Gauge className="w-4 h-4" /> Semáforo (NPS / CSAT)</h2>
          {historial.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="semaforo-empty">Este cliente aún no tiene respuestas de encuestas. Cuando se reciban vía webhook, aquí verás su puntaje por punto de contacto.</p>
          ) : (
            <div className="space-y-2.5">
              {/* Fila de 3 componentes simétricos: Semáforo · NPS en CJM · CSAT en CJM */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* 1. Último puntaje (última encuesta del cliente) */}
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Último puntaje</p>
                  {(() => {
                    const u = historial[0];
                    if (!u) return <p className="text-sm text-slate-400" data-testid="ultimo-puntaje-vacio">Sin encuestas.</p>;
                    const c = u.nps == null ? '#cbd5e1' : u.nps >= 9 ? '#16a34a' : u.nps >= 7 ? '#f59e0b' : '#dc2626';
                    return (
                      <div className="rounded-lg border border-slate-200 p-3" style={{ borderLeftColor: c, borderLeftWidth: 3 }} data-testid="ultimo-puntaje">
                        <p className="text-sm font-medium text-slate-800 flex items-center justify-between">
                          {u.punto_nombre || u.codigo_punto_contacto || 'Sin punto'}
                          {u.codigo_punto_contacto && <span className="text-[10px] font-mono text-slate-400">{u.codigo_punto_contacto}</span>}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDateTime(u.fecha)}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-sm">
                          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c }} />NPS <b>{u.nps ?? '—'}</b></span>
                          <span className="text-slate-500">CSAT {u.csat ?? '—'}</span>
                        </div>
                        {u.comentario && <p className="text-xs text-slate-500 mt-1 italic">"{u.comentario}"</p>}
                      </div>
                    );
                  })()}
                </div>

                {/* 2. NPS en CJM */}
                <CjmChart titulo="NPS en CJM" data={cjmData} dataKey="nps" color="#16a34a" domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} testid="grafico-nps-cjm" coloredByNps />

                {/* 3. CSAT en CJM */}
                <CjmChart titulo="CSAT en CJM" data={cjmData} dataKey="csat" color="#3b82f6" domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} testid="grafico-csat-cjm" />
              </div>

              {/* Historial (a todo el ancho, debajo de los 3 componentes) */}
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Historial de encuestas</p>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Fecha</TableHead>
                        <TableHead>Canal</TableHead>
                        <TableHead>Punto de contacto</TableHead>
                        <TableHead className="text-center">NPS</TableHead>
                        <TableHead className="text-center">CSAT</TableHead>
                        <TableHead>Área</TableHead>
                        <TableHead>Comentario</TableHead>
                        <TableHead>Proyecto</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historial.map((h) => (
                        <TableRow key={h.id} className="table-row">
                          <TableCell className="text-sm whitespace-nowrap">{fmtDateTime(h.fecha)}</TableCell>
                          <TableCell className="text-sm text-slate-500" data-testid={`historial-canal-${h.id}`}>{h.canal_atencion || h.codigo_canal || '—'}</TableCell>
                          <TableCell className="text-sm">{h.punto_nombre || '—'}</TableCell>
                          <TableCell className="text-center text-sm font-medium">{h.nps ?? '—'}</TableCell>
                          <TableCell className="text-center text-sm">{h.csat ?? '—'}</TableCell>
                          <TableCell className="text-sm text-slate-500">{h.area_funcional || '—'}</TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {h.comentario ? <span className="flex items-start gap-1"><MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-300" />{h.comentario}</span> : '—'}
                          </TableCell>
                          <TableCell className="text-sm text-slate-600 whitespace-nowrap" data-testid={`historial-proyecto-${h.id}`}>{h.nombre_proyecto || h.codigo_proyecto || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Cliente360DetailPage;
